import { useCallback, useEffect, useRef, useState } from "react";
import type { BlockerFunction } from "react-router-dom";

import shouldBlockPathChangeNavigation from "@/utils/shouldBlockPathChangeNavigation";

type UseUnsavedChangesGuardParams = {
  hasUnsavedChanges: boolean;
  isSubmitting?: boolean;
  onDiscard: () => void;
};

const useUnsavedChangesGuard = ({ hasUnsavedChanges, isSubmitting = false, onDiscard }: UseUnsavedChangesGuardParams) => {
  const [stateIsDiscardModalVisible, setStateIsDiscardModalVisible] = useState(false);
  const refSkipUnsavedChangesPrompt = useRef(false);

  useEffect(() => {
    if (hasUnsavedChanges) {
      refSkipUnsavedChangesPrompt.current = false;
    }
  }, [hasUnsavedChanges]);

  const markSaveNavigationAsSafe = useCallback(() => {
    refSkipUnsavedChangesPrompt.current = true;
  }, []);

  const resetSaveNavigationBypass = useCallback(() => {
    refSkipUnsavedChangesPrompt.current = false;
  }, []);

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setStateIsDiscardModalVisible(true);
      return;
    }
    onDiscard();
  }, [hasUnsavedChanges, onDiscard]);

  const handleConfirmDiscard = useCallback(() => {
    setStateIsDiscardModalVisible(false);
    refSkipUnsavedChangesPrompt.current = true;
    onDiscard();
  }, [onDiscard]);

  const handleCloseDiscardModal = useCallback(() => {
    setStateIsDiscardModalVisible(false);
  }, []);

  const shouldBlockNavigation = useCallback(
    ({ currentLocation, nextLocation }: Parameters<BlockerFunction>[0]) => {
      return shouldBlockPathChangeNavigation({
        skipUnsavedChangesPrompt: refSkipUnsavedChangesPrompt.current,
        hasUnsavedChanges,
        isSubmitting,
        currentPathname: currentLocation.pathname,
        nextPathname: nextLocation.pathname,
      });
    },
    [hasUnsavedChanges, isSubmitting],
  );

  return {
    handleCancel,
    handleCloseDiscardModal,
    handleConfirmDiscard,
    markSaveNavigationAsSafe,
    resetSaveNavigationBypass,
    shouldBlockNavigation,
    stateIsDiscardModalVisible,
  };
};

export default useUnsavedChangesGuard;
